'use client';

import { IChatbotAgent, IChatbotMessage } from '@/interfaces';
import { chatMessage, chatOpen, trackInteraction } from '@/lib/analytics';
import { renderChatMarkdownBlocks } from '@/lib/chatMarkdown';
import { IAgentForm } from '@/interfaces/obAgent';
import {
  fetchHistory,
  getLocalTimestamp,
  getStoredAgentSessionUuid,
  respondToForm,
  streamChat,
} from '@/lib/obAgentApi';
import {
  buildTranscript,
  EMPTY_READ_STATE,
  markTranscriptRead,
  newestServerId,
  nudgeDue,
  parseReadState,
  serializeReadState,
  type IReadState,
} from '@/lib/chat-transcript';
import { Language } from '@/lib/translations';
import { isBot } from '@/lib/user-agent';
import Image from 'next/image';
import { useParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PROACTIVE_GREETING_DELAY_MS = 10_000;
const INACTIVITY_NUDGE_DELAY_MS = 30 * 60 * 1000;
const NUDGE_CHECK_INTERVAL_MS = 60_000;
const HISTORY_POLL_INTERVAL_MS = 30_000;
/** Read watermark + synthetic-message flags. The transcript itself is never
 *  stored locally — the server history is the only copy (see chat-transcript). */
const READ_STATE_KEY = 'chat_read_state_v3';
/** Legacy local transcript keys, removed on first load after the migration. */
const LEGACY_STORAGE_KEYS = ['chat_messages_v2', 'chat_nudge_sent_v2'];
const NAVI_ANIMATED_AVATAR = '/assets/images/ai/navi-focused.gif';

function loadReadState(): IReadState {
  if (typeof window === 'undefined') return EMPTY_READ_STATE;
  try {
    return parseReadState(localStorage.getItem(READ_STATE_KEY));
  } catch {
    return EMPTY_READ_STATE;
  }
}

function saveReadState(state: IReadState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(READ_STATE_KEY, serializeReadState(state));
  } catch {
    // Private mode etc. — read state is a convenience only.
  }
}

function messageQuickReplies(message: IChatbotMessage): string[] {
  const raw = message.ai_chatbot_message_quick_replies_json;
  if (!raw) return [];

  try {
    let parsed: unknown = JSON.parse(raw);
    // A few older persisted messages were JSON-encoded twice. Accept them so
    // their replies do not silently disappear after a deployment or reload.
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return Array.isArray(parsed)
      ? parsed.filter((reply): reply is string => typeof reply === 'string')
      : [];
  } catch {
    return [];
  }
}

function formatMessageTime(timestamp: string, language: Language): string {
  const parsed = new Date(
    timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T')
  );
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString(language === 'en' ? 'en-US' : 'zh-TW', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The website assistant identity is static — the ob-agent backend has no
 * per-agent config (the legacy PHP chatbot's server-driven persona is gone).
 * Greeting + starter quick replies live here, per language.
 */
const STATIC_AGENT: IChatbotAgent = {
  ai_chatbot_id: 1,
  ai_chatbot_name_en: 'Navi',
  ai_chatbot_name_cn: 'Navi',
  ai_chatbot_role_en: 'AI assistant — answers instantly',
  ai_chatbot_role_cn: 'AI 智能助理 — 即時回覆',
  ai_chatbot_greeting_message_enabled: 1,
  ai_chatbot_greeting_message_en:
    "Hi! I'm Navi, the Omcean Booking AI assistant. Ask me anything about running your studio on OB — features, pricing, getting started…",
  ai_chatbot_greeting_message_cn:
    '您好！我是 Navi，Omcean Booking 的 AI 智能助理。關於 OB 的功能、價格、如何開始使用，都可以問我！',
  ai_chatbot_greeting_message_en_quick_replies_json: [
    'What does OB do?',
    'How much does it cost?',
    'How do I get started?',
  ],
  ai_chatbot_greeting_message_cn_quick_replies_json: [
    'OB 有哪些功能？',
    '價格是多少？',
    '如何開始使用？',
  ],
  ai_chatbot_nudge_message_enabled: 1,
  ai_chatbot_nudge_message_en:
    'Still exploring? I can help you compare plans, see the demo, or arrange a free consultation.',
  ai_chatbot_nudge_message_cn:
    '還在了解嗎？我可以幫您比較方案、查看示範，或安排免費諮詢。',
  ai_chatbot_form_submission_reply_en:
    "Thanks {name}! We've received your message — our team will get back to you by email shortly.",
  ai_chatbot_form_submission_reply_cn:
    '謝謝 {name}！我們已收到您的訊息，團隊會盡快透過電子郵件回覆您。',
  ai_chatbot_personality: null,
  ai_chatbot_profile_picture:
    'https://assets.omceanbooking.com/common/chatbot/focused.webp',
  ai_chatbot_is_admin: 0,
  ai_chatbot_active: 1,
  ai_chatbot_deleted: 0,
};

export default function ChatWidget() {
  const params = useParams();
  const pathname = usePathname();
  // Default to 'zh' when on root page (no lang param) or when lang is 'zh'
  const lang = (params?.lang === 'en' ? 'en' : 'zh') as Language;
  const chatLang = (params?.lang === 'en' ? 'en' : 'cn') as 'en' | 'cn';

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPopularActions, setShowPopularActions] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<IChatbotAgent | null>(
    null
  );
  const [isTyping, setIsTyping] = useState(false);
  const [showUnreadPopup, setShowUnreadPopup] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [pendingForm, setPendingForm] = useState<IAgentForm | null>(null);
  const [formDraft, setFormDraft] = useState<
    Record<string, string | boolean | string[]>
  >({});
  const [displayedMessageCount, setDisplayedMessageCount] = useState(5);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // ---- Transcript state: server rows + a read watermark + in-flight bubbles.
  const [serverMessages, setServerMessages] = useState<IChatbotMessage[]>([]);
  const [readState, setReadState] = useState<IReadState>(EMPTY_READ_STATE);
  /** Optimistic user bubble + streaming assistant bubble for the turn in
   *  progress (or a transient error bubble). Never persisted. */
  const [inflight, setInflight] = useState<IChatbotMessage[]>([]);
  /** Greeting shown as a pseudo-message (no agent turn, no server row). */
  const [greetingVisible, setGreetingVisible] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const popularActionsRef = useRef<HTMLDivElement>(null);
  const blockNewMessagesRef = useRef(true); // Block popups for first 10s after page load
  const chatVisibleRef = useRef(false);
  const isSendingRef = useRef(false);
  const langRef = useRef(lang);
  const readStateRef = useRef<IReadState>(EMPTY_READ_STATE);
  const serverMessagesRef = useRef<IChatbotMessage[]>([]);

  chatVisibleRef.current = isOpen && selectedAgent !== null;
  readStateRef.current = readState;
  serverMessagesRef.current = serverMessages;
  langRef.current = lang;

  // Listeners registered once (ob:open-chat) keep the first render's copy of
  // every state value, so the guard against a second send has to live in a
  // ref. Always set the two together.
  const setSending = (sending: boolean) => {
    isSendingRef.current = sending;
    setIsSendingMessage(sending);
  };

  // The assistant persona is static — no server round-trip, no localStorage.
  const agent = STATIC_AGENT;

  const buildGreeting = useCallback((): IChatbotMessage => {
    const now = getLocalTimestamp();
    const text =
      (chatLang === 'cn'
        ? agent.ai_chatbot_greeting_message_cn
        : agent.ai_chatbot_greeting_message_en) ?? '';
    const quickReplies =
      chatLang === 'cn'
        ? agent.ai_chatbot_greeting_message_cn_quick_replies_json
        : agent.ai_chatbot_greeting_message_en_quick_replies_json;
    return {
      ai_chatbot_message_id: null,
      ai_chatbot_message_role: 'assistant',
      ai_chatbot_message_text: text,
      ai_chatbot_message_quick_replies_json: quickReplies?.length
        ? JSON.stringify(quickReplies)
        : null,
      ai_chatbot_message_read: 1,
      ai_chatbot_message_type: 'website',
      ai_chatbot_message_insert_time: now,
    };
  }, [agent, chatLang]);

  const buildNudge = useCallback((): IChatbotMessage => {
    const text =
      (chatLang === 'cn'
        ? agent.ai_chatbot_nudge_message_cn
        : agent.ai_chatbot_nudge_message_en) ?? '';
    return {
      ai_chatbot_message_id: null,
      ai_chatbot_message_role: 'assistant',
      ai_chatbot_message_text: text,
      ai_chatbot_message_quick_replies_json: null,
      ai_chatbot_message_read: 1,
      ai_chatbot_message_type: 'website',
      ai_chatbot_message_insert_time: getLocalTimestamp(),
    };
  }, [agent, chatLang]);

  // Everything the panel renders, derived — no reconcile, no second copy.
  const transcript = useMemo(
    () =>
      buildTranscript({
        serverMessages,
        greeting:
          greetingVisible && agent.ai_chatbot_greeting_message_enabled === 1
            ? buildGreeting()
            : null,
        nudge: nudgeVisible ? buildNudge() : null,
        readState,
      }),
    [
      agent,
      buildGreeting,
      buildNudge,
      greetingVisible,
      nudgeVisible,
      readState,
      serverMessages,
    ]
  );
  const messages = useMemo(
    () => [...transcript.messages, ...inflight],
    [transcript.messages, inflight]
  );
  const unreadMessages = transcript.unread;

  const persistReadState = useCallback((next: IReadState) => {
    readStateRef.current = next;
    setReadState(next);
    saveReadState(next);
  }, []);

  // Everything visible is read: watermark to the newest server row, plus the
  // synthetic greeting/nudge if they are on screen.
  const markMessagesAsRead = useCallback(() => {
    const next = markTranscriptRead(
      readStateRef.current,
      serverMessagesRef.current,
      { greetingShown: greetingVisible, nudgeShown: nudgeVisible }
    );
    if (
      next.lastReadMessageId !== readStateRef.current.lastReadMessageId ||
      next.greetingRead !== readStateRef.current.greetingRead ||
      next.nudgeRead !== readStateRef.current.nudgeRead
    ) {
      persistReadState(next);
    }
    setShowUnreadPopup(false);
  }, [greetingVisible, nudgeVisible, persistReadState]);

  /**
   * Pull the server transcript. Returns true when the session has history.
   * `markIncomingRead` (the panel is open) advances the watermark so nothing
   * that just arrived is counted unread.
   */
  const refreshTranscript = useCallback(
    async (markIncomingRead = chatVisibleRef.current): Promise<boolean> => {
      if (!getStoredAgentSessionUuid()) return false;
      const rows = await fetchHistory();
      if (rows.length === 0) return false;

      const previousNewest = newestServerId(serverMessagesRef.current);
      serverMessagesRef.current = rows;
      setServerMessages(rows);
      const newest = newestServerId(rows);
      // The server has rows we hadn't seen: the turn is persisted, so any
      // optimistic bubbles (including a transient error bubble left behind by
      // a dropped stream) are superseded and would otherwise duplicate it.
      // Skipped mid-send so a streaming assistant bubble never flickers away.
      if (newest > previousNewest && !isSendingRef.current) setInflight([]);
      if (markIncomingRead) {
        if (newest > readStateRef.current.lastReadMessageId) {
          persistReadState({
            ...readStateRef.current,
            lastReadMessageId: newest,
          });
        }
        setShowUnreadPopup(false);
      } else if (
        newest > previousNewest &&
        newest > readStateRef.current.lastReadMessageId &&
        !blockNewMessagesRef.current
      ) {
        setShowUnreadPopup(true);
      }
      return true;
    },
    [persistReadState]
  );

  // Boot: drop the legacy local transcript, load the read watermark, fetch
  // the server history. First-time visitors get the greeting after a delay.
  useEffect(() => {
    try {
      for (const key of LEGACY_STORAGE_KEYS) localStorage.removeItem(key);
    } catch {
      // ignore
    }
    const stored = loadReadState();
    readStateRef.current = stored;
    setReadState(stored);
    if (getStoredAgentSessionUuid()) {
      setGreetingVisible(true);
      void refreshTranscript(false);
    }
  }, [refreshTranscript]);

  // Unblock new message popups after 10 seconds, then show any pending unread messages
  useEffect(() => {
    const timer = setTimeout(() => {
      blockNewMessagesRef.current = false;
      if (!isOpen && transcript.unread.length > 0) setShowUnreadPopup(true);
    }, 10000);
    return () => clearTimeout(timer);
    // Only the block window matters; unread changes are handled by the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle scroll detection for auto-scroll behavior
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Check if user is near the bottom (within 50px)
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      50;
    setIsAtBottom(isNearBottom);
  }, []);

  // Auto-scroll when streaming or new messages arrive (only if at bottom)
  useEffect(() => {
    if (isAtBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAtBottom]);

  // Scroll to bottom of messages
  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: instant ? 'instant' : 'smooth',
    });
  };

  useEffect(() => {
    scrollToBottom(true);
  }, [messages]);

  // The contact/consultant forms send a real chat turn; they tell the widget
  // to pull the new rows and surface them as unread.
  useEffect(() => {
    const handleTranscriptChanged = () => {
      void refreshTranscript(chatVisibleRef.current);
      if (isSendingRef.current) {
        setInputValue('');
        setSending(false);
      }
      if (chatVisibleRef.current) setIsTyping(false);
    };
    window.addEventListener('chatTranscriptChanged', handleTranscriptChanged);
    return () =>
      window.removeEventListener(
        'chatTranscriptChanged',
        handleTranscriptChanged
      );
  }, [refreshTranscript]);

  // Let any page open the assistant, optionally asking a question straight away.
  useEffect(() => {
    const handleOpenChat = (event: Event) => {
      const detail = (
        event as CustomEvent<{ message?: string; source?: string }>
      ).detail;
      const question = detail?.message;
      // An open from a page is an open like any other: it belongs in the
      // funnel, and the close event measures against its timestamp.
      if (!chatVisibleRef.current) {
        chatOpen(langRef.current, detail?.source ?? 'ask_navi');
        markChatOpened();
        document.body.style.overflow = 'hidden';
      }
      setIsOpen(true);
      startChat();
      if (question) {
        window.setTimeout(() => {
          void sendMessageRef.current(question, 'quick_reply');
        }, 300);
      }
    };
    window.addEventListener('ob:open-chat', handleOpenChat);
    return () => window.removeEventListener('ob:open-chat', handleOpenChat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark messages as read when chat is opened with a selected agent
  useEffect(() => {
    if (isOpen && selectedAgent) markMessagesAsRead();
  }, [isOpen, selectedAgent, markMessagesAsRead, transcript.unread.length]);

  // Focus the composer as soon as the chat opens.
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, [inputValue]);

  useEffect(() => {
    if (!showPopularActions) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (
        popularActionsRef.current &&
        !popularActionsRef.current.contains(event.target as Node)
      ) {
        setShowPopularActions(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [showPopularActions]);

  // Proactive greeting for a first-time visitor: a free pseudo-message shown
  // (unread) after a delay while the panel is closed. Nothing is stored.
  useEffect(() => {
    if (
      isBot() ||
      agent.ai_chatbot_greeting_message_enabled !== 1 ||
      isOpen ||
      greetingVisible ||
      getStoredAgentSessionUuid()
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (chatVisibleRef.current) return;
      setGreetingVisible(true);
      if (!blockNewMessagesRef.current) setShowUnreadPopup(true);
    }, PROACTIVE_GREETING_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [agent, greetingVisible, isOpen]);

  // Send the configured follow-up once when the latest assistant reply has
  // been idle for 30 minutes. It is a pseudo-message; `nudgeRead` in the read
  // state records that it was seen so it does not reappear on reload.
  useEffect(() => {
    if (isBot() || agent.ai_chatbot_nudge_message_enabled !== 1) return;
    const checkForNudge = () => {
      if (nudgeVisible) return;
      if (
        nudgeDue({
          serverMessages: serverMessagesRef.current,
          now: Date.now(),
          delayMs: INACTIVITY_NUDGE_DELAY_MS,
          alreadyShown: readStateRef.current.nudgeRead,
        })
      ) {
        setNudgeVisible(true);
        if (!chatVisibleRef.current && !blockNewMessagesRef.current) {
          setShowUnreadPopup(true);
        }
      }
    };
    checkForNudge();
    const interval = window.setInterval(checkForNudge, NUDGE_CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [agent, nudgeVisible]);

  // Reconcile persisted assistant/human replies after interrupted streams and
  // while a visitor keeps the page open.
  useEffect(() => {
    if (isBot()) return;

    const pollHistory = () => {
      if (
        document.visibilityState === 'visible' &&
        !isSendingRef.current &&
        getStoredAgentSessionUuid()
      ) {
        void refreshTranscript();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') pollHistory();
    };

    const interval = window.setInterval(pollHistory, HISTORY_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshTranscript]);

  // Start a chat with the support agent
  const startChat = () => {
    setSelectedAgent(agent);
    setIsTyping(false);
    setGreetingVisible(true);
    if (getStoredAgentSessionUuid()) void refreshTranscript(true);
  };

  // Send message — streams the answer from ob-agent (real deltas, no polling).
  const sendMessage = async (
    messageOverride?: string,
    source:
      | 'typed'
      | 'quick_reply'
      | 'popular_action'
      | 'popup_quick_reply' = messageOverride ? 'quick_reply' : 'typed'
  ) => {
    const messageText = (messageOverride ?? inputValue).trim();
    if (!messageText || isSendingRef.current) return;

    setSending(true);
    setInputValue('');
    setShowPopularActions(false);
    setIsTyping(true);

    const now = getLocalTimestamp();
    const userMessage: IChatbotMessage = {
      ai_chatbot_message_id: null,
      ai_chatbot_message_role: 'user',
      ai_chatbot_message_text: messageText,
      ai_chatbot_message_quick_replies_json: null,
      ai_chatbot_message_read: 1,
      ai_chatbot_message_type: 'website',
      ai_chatbot_message_insert_time: now,
      ai_chatbot_message_read_time: now,
    };

    // Optimistic bubbles for THIS turn only; the server copy replaces them
    // once the turn is persisted.
    const previousNewest = newestServerId(serverMessagesRef.current);
    setInflight([userMessage]);

    // Track message sent event (php telemetry + GA + Clarity)
    chatMessage(true, lang, source);

    // One growing assistant message, updated in place as deltas arrive.
    // Suggestions live in the closure so delta/suggestions/done can arrive in
    // any order without wiping each other's state.
    let assistantText = '';
    let assistantSuggestions: string[] = [];
    const assistantMessage = (): IChatbotMessage => ({
      ai_chatbot_message_id: null,
      ai_chatbot_message_role: 'assistant',
      ai_chatbot_message_text: assistantText,
      ai_chatbot_message_quick_replies_json: assistantSuggestions.length
        ? JSON.stringify(assistantSuggestions)
        : null,
      ai_chatbot_message_read: 1,
      ai_chatbot_message_type: 'website',
      ai_chatbot_message_insert_time: getLocalTimestamp(),
      ai_chatbot_message_read_time: getLocalTimestamp(),
    });
    const updateAssistant = () => {
      setInflight([userMessage, assistantMessage()]);
    };

    const errorText = (retryable: boolean): string =>
      chatLang === 'cn'
        ? retryable
          ? '目前使用人數較多，請稍後再試。'
          : '抱歉，出了點問題，請再試一次。'
        : retryable
          ? "I'm a bit busy right now — please try again in a moment."
          : 'Sorry, something went wrong. Please try again.';

    // Replace the optimistic bubbles with the persisted rows. Keep them until
    // the server actually has the new turn, so nothing flickers away.
    const settleFromServer = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        const ok = await refreshTranscript(true);
        if (ok && newestServerId(serverMessagesRef.current) > previousNewest) {
          setInflight([]);
          return;
        }
        await new Promise(resolve => window.setTimeout(resolve, 700));
      }
      // Persistence lagged: leave the optimistic bubbles; the poll will sync.
    };

    await streamChat(
      messageText,
      {
        onDelta: text => {
          assistantText += text;
          setIsTyping(false);
          updateAssistant();
        },
        onSuggestions: suggestions => {
          assistantSuggestions = suggestions;
          updateAssistant();
        },
        onDone: fullText => {
          if (fullText) assistantText = fullText;
          setIsTyping(false);
          setPendingForm(null);
          updateAssistant();
          // Track message received event (GA + Clarity)
          chatMessage(false, lang);
          void settleFromServer();
        },
        onError: (errorMessage, retryable) => {
          trackInteraction(
            'chat_reply_failed',
            retryable ? 'retryable' : 'fatal',
            { language: lang, error: errorMessage.slice(0, 200) }
          );
          assistantText = errorText(retryable);
          setIsTyping(false);
          setPendingForm(null);
          // Transient: an error bubble is shown but never becomes history.
          updateAssistant();
        },
        onForm: form => {
          setIsTyping(false);
          setFormDraft({});
          setPendingForm(form);
        },
      },
      pathname ?? undefined,
      lang
    );

    setSending(false);
  };

  // The ob:open-chat listener is registered once, so it reaches the current
  // sendMessage — with this page's path and language — through a ref.
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  const markChatOpened = () => {
    (window as Window & { chatOpenTime?: number }).chatOpenTime = Date.now();
  };

  // Toggle chat visibility
  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      startChat();
      // Track chat opened
      chatOpen(lang);
      // Prevent body scrolling when chat is open
      document.body.style.overflow = 'hidden';
      // Scroll to the latest message after the direct-to-chat panel mounts.
      setTimeout(() => scrollToBottom(true), 100);
    } else {
      const openedAt = (window as Window & { chatOpenTime?: number })
        .chatOpenTime;
      trackInteraction('chat_closed', null, {
        language: lang,
        open_seconds: openedAt
          ? Math.round((Date.now() - openedAt) / 1000)
          : null,
      });
      // Restore body scrolling when chat is closed
      document.body.style.overflow = 'unset';
    }

    // Record when chat was opened for time tracking
    if (newIsOpen) markChatOpened();
  };

  // Clean up body scroll lock when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Answer / dismiss the agent's in-chat form (the run resumes on the SAME
  // open stream after respond succeeds).
  const submitPendingForm = async (cancelled: boolean) => {
    if (!pendingForm) return;
    trackInteraction(
      cancelled ? 'chat_form_cancelled' : 'chat_form_submitted',
      pendingForm.title ?? null,
      { language: lang, questions: pendingForm.questions.length }
    );
    const ok = await respondToForm(pendingForm.requestId, {
      answers: cancelled ? {} : formDraft,
      cancelled: cancelled || undefined,
    });
    if (ok) {
      setPendingForm(null);
      setIsTyping(true); // the model is composing its follow-up
    }
  };

  const formComplete =
    pendingForm?.questions.every(q => {
      const v = formDraft[q.id];
      if (q.type === 'confirm') return typeof v === 'boolean';
      if (q.type === 'multi_choice') return Array.isArray(v) && v.length > 0;
      return typeof v === 'string' && v.trim().length > 0;
    }) ?? false;

  const renderPendingForm = () => {
    if (!pendingForm) return null;
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-[90%] rounded-lg border border-[#3880ff] bg-white dark:bg-gray-700 p-3 space-y-3">
          {pendingForm.title && (
            <div
              className="text-sm font-semibold"
              style={{ color: 'var(--color-foreground)' }}
            >
              {pendingForm.title}
            </div>
          )}
          {pendingForm.description && (
            <div className="text-xs text-gray-500 dark:text-gray-300">
              {pendingForm.description}
            </div>
          )}
          {pendingForm.questions.map(q => (
            <div key={q.id} className="space-y-1">
              <div
                className="text-xs font-medium"
                style={{ color: 'var(--color-foreground)' }}
              >
                {q.question}
              </div>
              {q.type === 'text' && (
                <input
                  type="text"
                  value={(formDraft[q.id] as string) ?? ''}
                  placeholder={q.placeholder ?? ''}
                  onChange={e =>
                    setFormDraft(prev => ({ ...prev, [q.id]: e.target.value }))
                  }
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3880ff]"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-foreground)',
                  }}
                />
              )}
              {q.type === 'confirm' && (
                <div className="flex gap-1.5">
                  {[
                    { value: true, label: chatLang === 'cn' ? '是' : 'Yes' },
                    { value: false, label: chatLang === 'cn' ? '否' : 'No' },
                  ].map(option => (
                    <button
                      key={String(option.value)}
                      type="button"
                      aria-pressed={formDraft[q.id] === option.value}
                      onClick={() =>
                        setFormDraft(prev => ({
                          ...prev,
                          [q.id]: option.value,
                        }))
                      }
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        formDraft[q.id] === option.value
                          ? 'bg-[#3880ff] text-white border-[#3880ff]'
                          : 'bg-white dark:bg-gray-700 text-[#3880ff] dark:text-blue-300 border-[#3880ff] dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              {(q.type === 'choice' || q.type === 'suggestion') && (
                <div className="flex flex-wrap gap-1.5">
                  {(q.options ?? []).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() =>
                        setFormDraft(prev => ({ ...prev, [q.id]: option }))
                      }
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        formDraft[q.id] === option
                          ? 'bg-[#3880ff] text-white border-[#3880ff]'
                          : 'bg-white dark:bg-gray-700 text-[#3880ff] dark:text-blue-300 border-[#3880ff] dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
              {q.type === 'multi_choice' && (
                <div className="flex flex-wrap gap-1.5">
                  {(q.options ?? []).map(option => {
                    const selected = Array.isArray(formDraft[q.id])
                      ? (formDraft[q.id] as string[]).includes(option)
                      : false;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() =>
                          setFormDraft(prev => {
                            const current = Array.isArray(prev[q.id])
                              ? (prev[q.id] as string[])
                              : [];
                            return {
                              ...prev,
                              [q.id]: selected
                                ? current.filter(v => v !== option)
                                : [...current, option],
                            };
                          })
                        }
                        className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                          selected
                            ? 'bg-[#3880ff] text-white border-[#3880ff]'
                            : 'bg-white dark:bg-gray-700 text-[#3880ff] dark:text-blue-300 border-[#3880ff] dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              disabled={!formComplete}
              onClick={() => submitPendingForm(false)}
              className={`px-3 py-1.5 text-sm rounded-lg text-white transition-colors ${
                formComplete
                  ? 'bg-[#3880ff] hover:bg-[#2563eb] cursor-pointer'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
              }`}
            >
              {chatLang === 'cn' ? '送出' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={() => submitPendingForm(true)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
            >
              {chatLang === 'cn' ? '取消' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render chat view
  const renderChatView = () => {
    const displayedMessages = messages.slice(-displayedMessageCount);
    const popularActions =
      chatLang === 'cn'
        ? ['OB 有哪些功能？', '價格是多少？', '如何開始使用？']
        : [
            'What does OB do?',
            'How much does it cost?',
            'How do I get started?',
          ];

    return (
      <div className="flex h-full min-h-0 flex-col">
        <header
          className="flex flex-shrink-0 items-center justify-between border-b border-black/[0.08] px-4 py-4 dark:border-white/[0.08]"
          style={{ backgroundColor: 'var(--color-card-background)' }}
        >
          <div className="flex min-w-0 items-center gap-3">
            {selectedAgent && (
              <>
                <span className="relative h-[42px] w-[42px] flex-shrink-0 overflow-hidden rounded-full">
                  <Image
                    src={selectedAgent.ai_chatbot_profile_picture}
                    alt=""
                    width={42}
                    height={42}
                    className="h-full w-full object-cover"
                  />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <strong
                    className="truncate text-base leading-5"
                    style={{ color: 'var(--color-foreground)' }}
                  >
                    {lang === 'en' ? 'Ask Navi' : '詢問 Navi'}
                  </strong>
                  <small
                    className="truncate text-xs font-medium"
                    style={{ color: 'var(--color-foreground-secondary)' }}
                  >
                    {lang === 'en'
                      ? 'Your booking assistant'
                      : '您的預約智能助理'}
                  </small>
                </span>
              </>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={() => setIsExpanded(value => !value)}
              className="hidden h-8 w-8 cursor-pointer place-items-center rounded-[10px] border-0 bg-transparent text-gray-500 transition-colors hover:bg-black/[0.06] hover:text-gray-900 sm:grid dark:text-gray-400 dark:hover:bg-white/[0.07] dark:hover:text-white"
              aria-label={
                isExpanded
                  ? lang === 'en'
                    ? 'Collapse chat'
                    : '縮小聊天視窗'
                  : lang === 'en'
                    ? 'Expand chat'
                    : '展開聊天視窗'
              }
              title={
                isExpanded
                  ? lang === 'en'
                    ? 'Collapse'
                    : '縮小'
                  : lang === 'en'
                    ? 'Expand'
                    : '展開'
              }
            >
              {isExpanded ? (
                <svg
                  className="h-[18px] w-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 4v5H4m11 11v-5h5M4 9l6-6m4 18 6-6"
                  />
                </svg>
              ) : (
                <svg
                  className="h-[18px] w-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 4h5v5M9 20H4v-5M20 4l-6 6M4 20l6-6"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={toggleChat}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-[10px] border-0 bg-transparent text-gray-500 transition-colors hover:bg-black/[0.06] hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/[0.07] dark:hover:text-white"
              aria-label={lang === 'en' ? 'Close chat' : '關閉聊天'}
              title={lang === 'en' ? 'Close' : '關閉'}
            >
              <svg
                className="h-[18px] w-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
              >
                <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        </header>

        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
        >
          {messages.length > displayedMessageCount && (
            <button
              type="button"
              onClick={() => setDisplayedMessageCount(prev => prev + 5)}
              className="w-full cursor-pointer py-1 text-sm font-medium text-[#3880ff] transition-colors hover:text-[#2563eb]"
            >
              {lang === 'en' ? 'Load earlier messages' : '載入較早的訊息'}
            </button>
          )}

          {displayedMessages.map((message, index) => {
            const isUser = message.ai_chatbot_message_role === 'user';
            const isLatestMessage = index === displayedMessages.length - 1;
            const generatedReplies = messageQuickReplies(message);
            // The suggestion model is deliberately non-blocking, so a failed
            // side-call can legitimately leave a completed answer with no
            // chips. Keep the interface useful with the same safe starters
            // shown in Popular actions instead of rendering an empty gap.
            const replies =
              generatedReplies.length > 0
                ? generatedReplies
                : !isUser && isLatestMessage && !isTyping && !pendingForm
                  ? popularActions
                  : [];
            const time = formatMessageTime(
              message.ai_chatbot_message_insert_time,
              lang
            );
            const showReplies =
              !isUser &&
              isLatestMessage &&
              !isSendingMessage &&
              replies.length > 0;

            return (
              <div
                key={`${message.ai_chatbot_message_id ?? 'msg'}-${index}`}
                className={`flex flex-shrink-0 ${isUser ? 'justify-end' : 'w-full justify-start'}`}
              >
                {isUser ? (
                  <div className="flex max-w-[78%] flex-col items-end">
                    <div className="rounded-2xl bg-[#3880ff] px-3.5 py-2.5 text-sm leading-[1.45] text-white shadow-sm">
                      <div
                        className="chat-markdown"
                        dangerouslySetInnerHTML={{
                          __html: renderChatMarkdownBlocks(
                            message.ai_chatbot_message_text
                          ),
                        }}
                      />
                    </div>
                    {time && (
                      <span className="mt-1 inline-flex items-center text-[11.5px] text-gray-500 dark:text-gray-400">
                        {time}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="min-w-0 w-full">
                    <div className="mb-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-500 dark:text-gray-400">
                      <Image
                        src={
                          selectedAgent?.ai_chatbot_profile_picture ??
                          agent.ai_chatbot_profile_picture
                        }
                        alt=""
                        width={20}
                        height={20}
                        className="h-5 w-5 rounded-full object-cover"
                      />
                      <span>
                        {chatLang === 'cn'
                          ? agent.ai_chatbot_name_cn
                          : agent.ai_chatbot_name_en}
                      </span>
                    </div>
                    <div
                      className="chat-markdown text-[15px] leading-6 text-gray-800 dark:text-gray-100"
                      dangerouslySetInnerHTML={{
                        __html: renderChatMarkdownBlocks(
                          message.ai_chatbot_message_text
                        ),
                      }}
                    />
                    {time && (
                      <span className="mt-1 inline-flex items-center text-[11.5px] text-gray-500 dark:text-gray-400">
                        {time}
                      </span>
                    )}
                    {showReplies && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {replies.map(reply => (
                          <button
                            type="button"
                            key={reply}
                            onClick={() => void sendMessage(reply)}
                            className="min-h-[30px] cursor-pointer rounded-full border border-[#3880ff]/35 bg-[#3880ff]/[0.06] px-3 py-1 text-[13px] font-medium text-[#2563eb] transition-colors hover:border-[#3880ff]/60 hover:bg-[#3880ff]/[0.11] dark:text-blue-300"
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {renderPendingForm()}

          {isTyping && (
            <div className="w-full flex-shrink-0">
              <div className="mb-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-500 dark:text-gray-400">
                <Image
                  src={
                    selectedAgent?.ai_chatbot_profile_picture ??
                    agent.ai_chatbot_profile_picture
                  }
                  alt=""
                  width={20}
                  height={20}
                  className="h-5 w-5 rounded-full object-cover"
                />
                <span>
                  {chatLang === 'cn'
                    ? agent.ai_chatbot_name_cn
                    : agent.ai_chatbot_name_en}
                </span>
              </div>
              <div
                className="flex items-center gap-1.5 py-1"
                aria-label={lang === 'en' ? 'Navi is typing' : 'Navi 正在輸入'}
              >
                {[0, 150, 300].map(delay => (
                  <span
                    key={delay}
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div
          className="flex-shrink-0 px-3 pb-3 pt-2"
          style={{ backgroundColor: 'var(--color-card-background)' }}
        >
          <form
            data-chat-form="true"
            onSubmit={event => {
              event.preventDefault();
              event.stopPropagation();
              void sendMessage();
            }}
            className="flex flex-col gap-1 rounded-[14px] border border-black/[0.12] bg-white px-3.5 py-2 transition-colors focus-within:border-[#3880ff]/50 dark:border-white/[0.12] dark:bg-gray-900"
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={inputValue}
              onChange={event => setInputValue(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (inputValue.trim() && !isSendingMessage) {
                    void sendMessage();
                  }
                }
              }}
              disabled={isSendingMessage}
              placeholder={
                chatLang === 'cn' ? '詢問 Navi…' : 'Ask Navi anything…'
              }
              className="min-h-[38px] max-h-[120px] w-full resize-none overflow-y-auto border-0 bg-transparent px-0 py-1 text-[13px] leading-[1.45] text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:placeholder:text-gray-500"
            />
            <div className="flex items-center justify-between gap-2">
              <div ref={popularActionsRef} className="relative min-w-0">
                <button
                  type="button"
                  onClick={() => setShowPopularActions(value => !value)}
                  className="inline-flex cursor-pointer items-center gap-1 bg-transparent py-0.5 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-expanded={showPopularActions}
                >
                  <svg
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="m12 2 1.45 4.05L17.5 7.5l-4.05 1.45L12 13l-1.45-4.05L6.5 7.5l4.05-1.45L12 2Zm6 10 .9 2.1L21 15l-2.1.9L18 18l-.9-2.1L15 15l2.1-.9L18 12ZM6 14l1.18 2.82L10 18l-2.82 1.18L6 22l-1.18-2.82L2 18l2.82-1.18L6 14Z" />
                  </svg>
                  <span>{lang === 'en' ? 'Popular actions' : '熱門操作'}</span>
                  <svg
                    className={`h-3 w-3 transition-transform ${showPopularActions ? 'rotate-180' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m6 9 6 6 6-6"
                    />
                  </svg>
                </button>
                {showPopularActions && (
                  <div className="absolute bottom-[calc(100%+8px)] left-0 z-20 w-56 overflow-hidden rounded-xl border border-black/[0.1] bg-white p-1.5 shadow-xl dark:border-white/[0.1] dark:bg-gray-800">
                    {popularActions.map(action => (
                      <button
                        type="button"
                        key={action}
                        onClick={() =>
                          void sendMessage(action, 'popular_action')
                        }
                        className="block w-full cursor-pointer rounded-lg px-3 py-2 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={isSendingMessage || !inputValue.trim()}
                className="grid h-7 w-7 flex-shrink-0 cursor-pointer place-items-center rounded-[7px] border-0 bg-gray-900 text-white transition-all hover:bg-black disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-400 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-500"
                aria-label={lang === 'en' ? 'Send message' : '傳送訊息'}
              >
                {isSendingMessage ? (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 19V5m-6 6 6-6 6 6"
                    />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Chat Window Container */}
      {isOpen && (
        <div
          className="fixed bottom-20 right-3 sm:bottom-24 sm:right-6"
          style={{ zIndex: 999999 }}
        >
          {/* Chat Window */}
          <div
            className={`flex flex-col overflow-hidden rounded-[22px] border border-black/[0.1] shadow-[0_18px_52px_rgba(0,0,0,0.2)] animate-fade-in-up transition-[width,height] duration-200 dark:border-white/[0.1] ${
              isExpanded
                ? 'h-[min(720px,calc(100dvh-104px))] w-[min(720px,calc(100vw-48px))] sm:h-[min(720px,calc(100dvh-116px))]'
                : 'h-[min(560px,calc(100dvh-104px))] w-[min(420px,calc(100vw-24px))] sm:h-[min(560px,calc(100dvh-116px))]'
            }`}
            style={{ backgroundColor: 'var(--color-card-background)' }}
          >
            <div
              className="flex-1 overflow-hidden flex flex-col"
              style={{ backgroundColor: 'var(--color-background-secondary)' }}
            >
              {renderChatView()}
            </div>
          </div>
        </div>
      )}

      {/* Unread Message Popup */}
      {showUnreadPopup && !isOpen && unreadMessages.length > 0 && agent && (
        <div className="fixed bottom-20 right-3 z-50 w-[min(360px,calc(100vw-24px))] animate-[slideUp_0.3s_ease-out] sm:bottom-24 sm:right-6">
          <div
            className="relative rounded-2xl border border-gray-200/70 bg-white/90 p-4 shadow-2xl backdrop-blur-xl dark:border-gray-700/70 dark:bg-gray-800/90"
            style={{
              boxShadow:
                '0 25px 35px -10px rgba(0, 0, 0, 0.15), 0 15px 15px -8px rgba(0, 0, 0, 0.08), 0 8px 8px -4px rgba(0, 0, 0, 0.04)',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setShowUnreadPopup(false);
                // Mark messages as read so popup doesn't reappear on reload
                markMessagesAsRead();
              }}
              className="absolute right-2 top-2 grid h-8 w-8 cursor-pointer place-items-center rounded-[10px] bg-transparent text-gray-400 transition-colors hover:bg-black/[0.06] hover:text-gray-700 dark:text-gray-500 dark:hover:bg-white/[0.07] dark:hover:text-gray-200"
              aria-label="Close message"
            >
              <svg
                className="h-[18px] w-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
              >
                <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
            <div className="flex items-start space-x-3">
              <Image
                src={agent.ai_chatbot_profile_picture}
                alt={
                  chatLang === 'cn'
                    ? agent.ai_chatbot_name_cn
                    : agent.ai_chatbot_name_en
                }
                width={36}
                height={36}
                className="h-10 w-10 flex-shrink-0 rounded-full object-cover ring-1 ring-gray-200 shadow-sm dark:ring-gray-600"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {chatLang === 'cn'
                      ? agent.ai_chatbot_name_cn
                      : agent.ai_chatbot_name_en}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {chatLang === 'cn'
                      ? agent.ai_chatbot_role_cn
                      : agent.ai_chatbot_role_en}
                  </span>
                </div>

                {/* Show unread messages - max 3 lines with ellipsis */}
                <div className="mb-2">
                  {unreadMessages.slice(0, 1).map((message, msgIndex) => {
                    const quickReplies = messageQuickReplies(message);
                    return (
                      <div key={message.ai_chatbot_message_id ?? msgIndex}>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-3">
                          {message.ai_chatbot_message_text
                            .replace(/<[^>]*>/g, '')
                            .replace(/\*\*(.*?)\*\*/g, '$1')
                            .replace(/\*(.*?)\*/g, '$1')
                            .replace(/`(.*?)`/g, '$1')
                            .trim()}
                        </p>
                        {quickReplies && quickReplies.length > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                            {quickReplies
                              .slice(0, 3)
                              .map((reply: string, idx: number) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    startChat();
                                    setIsOpen(true);
                                    chatOpen(lang, 'popup_quick_reply');
                                    markChatOpened();
                                    setShowUnreadPopup(false);
                                    window.setTimeout(
                                      () =>
                                        void sendMessage(
                                          reply,
                                          'popup_quick_reply'
                                        ),
                                      100
                                    );
                                  }}
                                  className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline cursor-pointer"
                                >
                                  {reply}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => {
                    startChat();
                    setIsOpen(true);
                    chatOpen(lang, 'unread_popup');
                    markChatOpened();
                    setShowUnreadPopup(false);
                  }}
                  className="rounded-full bg-[#3880ff] px-3 py-1.5 text-xs font-medium text-white transition-colors duration-200 hover:bg-[#2563eb] cursor-pointer"
                >
                  {chatLang === 'cn' ? '回覆' : 'Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Frontend-style assistant launcher */}
      <div
        className="fixed bottom-0 right-0 p-3 sm:p-6"
        style={{ zIndex: 999999, overflowX: 'hidden' }}
      >
        <button
          type="button"
          onClick={toggleChat}
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? lang === 'en'
                ? 'Close chat'
                : '關閉聊天'
              : lang === 'en'
                ? 'Ask Navi'
                : '詢問 Navi'
          }
          className={`navi-launcher group relative inline-flex h-[42px] cursor-pointer items-center gap-[7px] overflow-hidden rounded-full border border-white/20 bg-gradient-to-br from-[#2563eb] via-[#3880ff] to-[#5b8dff] px-2 text-white shadow-[0_8px_24px_rgba(56,128,255,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(56,128,255,0.36)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3880ff] focus-visible:ring-offset-2 ${isOpen ? 'is-open' : ''}`}
        >
          <span
            className={`relative z-[1] grid h-[30px] w-[30px] flex-shrink-0 place-items-center overflow-hidden rounded-full ${isOpen ? '' : 'bg-white/15 ring-1 ring-white/30'}`}
          >
            {isOpen ? (
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                aria-hidden="true"
              >
                <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
              </svg>
            ) : (
              <Image
                unoptimized
                src={NAVI_ANIMATED_AVATAR}
                alt=""
                width={30}
                height={30}
                className="h-full w-full object-cover"
              />
            )}
          </span>
          <span className="relative z-[1] grid whitespace-nowrap text-sm font-semibold">
            <span
              className="invisible col-start-1 row-start-1"
              aria-hidden="true"
            >
              {lang === 'en' ? 'Ask Navi' : '詢問 Navi'}
            </span>
            <span className="col-start-1 row-start-1">
              {isOpen
                ? lang === 'en'
                  ? 'Close'
                  : '關閉'
                : lang === 'en'
                  ? 'Ask Navi'
                  : '詢問 Navi'}
            </span>
          </span>
          {!isOpen && unreadMessages.length > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 z-[2] h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500"
              aria-hidden="true"
            />
          )}
        </button>
      </div>

      {/* Styles for animations and scrollbar hiding */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }

        .navi-launcher::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(
            110deg,
            transparent 24%,
            rgba(255, 255, 255, 0.12) 38%,
            rgba(255, 255, 255, 0.5) 50%,
            rgba(255, 255, 255, 0.12) 62%,
            transparent 76%
          );
          background-position: 100% 0;
          background-repeat: no-repeat;
          background-size: 300% 100%;
          animation: navi-launcher-shimmer 3.4s ease-in-out infinite;
          pointer-events: none;
        }

        .navi-launcher.is-open::before {
          animation: none;
          opacity: 0;
        }

        @keyframes navi-launcher-shimmer {
          0%,
          48% {
            background-position: 100% 0;
          }
          82%,
          100% {
            background-position: 0 0;
          }
        }

        /* Hide scrollbars specifically for chat containers */
        .overflow-y-auto::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }

        .overflow-y-auto {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }

        @media (prefers-reduced-motion: reduce) {
          .navi-launcher::before,
          .animate-fade-in-up {
            animation: none;
          }
        }
      `}</style>
      <style jsx global>{`
        .chat-markdown > :first-child {
          margin-top: 0;
        }

        .chat-markdown > :last-child {
          margin-bottom: 0;
        }

        .chat-markdown p {
          margin: 0 0 0.65rem;
        }

        .chat-markdown ul,
        .chat-markdown ol {
          margin: 0.45rem 0 0.75rem;
          padding-left: 1.25rem;
        }

        .chat-markdown ul {
          list-style: disc;
        }

        .chat-markdown ol {
          list-style: decimal;
        }

        .chat-markdown li + li {
          margin-top: 0.25rem;
        }

        .chat-markdown h3,
        .chat-markdown h4,
        .chat-markdown h5 {
          margin: 0.75rem 0 0.35rem;
          font-size: 1em;
          font-weight: 700;
        }

        .chat-markdown a {
          color: #3880ff;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        /* Same-site action link (e.g. the live demo) — an orange button that
           keeps the visitor on this domain, not a blue new-tab link. */
        .chat-markdown a.chat-cta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 4px;
          padding: 8px 16px;
          border-radius: 9999px;
          background: #f97316;
          color: #fff;
          font-weight: 600;
          text-decoration: none;
        }

        .chat-markdown a.chat-cta:hover {
          background: #ea580c;
        }

        .chat-markdown code {
          border-radius: 4px;
          background: rgba(15, 23, 42, 0.07);
          padding: 0.08rem 0.3rem;
          font-size: 0.9em;
        }

        .chat-markdown .ob-md-table-wrap {
          max-width: 100%;
          margin: 0.75rem 0;
          overflow-x: auto;
          border: 1px solid rgba(15, 23, 42, 0.1);
          border-radius: 12px;
        }

        .chat-markdown table {
          width: 100%;
          min-width: 360px;
          border-collapse: collapse;
          font-size: 0.88em;
        }

        .chat-markdown th,
        .chat-markdown td {
          padding: 0.55rem 0.65rem;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          text-align: left;
          vertical-align: top;
        }

        .chat-markdown th {
          background: rgba(15, 23, 42, 0.04);
          font-weight: 700;
        }

        .chat-markdown tbody tr:last-child td {
          border-bottom: 0;
        }

        .dark .chat-markdown code,
        .dark .chat-markdown th {
          background: rgba(255, 255, 255, 0.07);
        }

        .dark .chat-markdown .ob-md-table-wrap,
        .dark .chat-markdown th,
        .dark .chat-markdown td {
          border-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </>
  );
}
